import { Box } from '@mui/material';
import React from 'react';

import { colors } from '../../theme/colors/colors.js';
import { Text } from '../Typography/index.js';

type LoaderColor = 'primary' | 'gray' | 'white' | 'black';
type LoaderVariant = 'bar' | 'dot';

const LOADER_COLORS: Record<LoaderColor, string> = {
  primary: colors.primary.default,
  gray: colors.content.dark,
  black: colors.common.black,
  white: colors.common.white,
};

interface LoaderProps {
  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * The color of the dot pulse or the moving bar (depending on variant)
   * @default 'primary'
   */
  color?: LoaderColor;

  /**
   * The label for the Loader.
   * Only applicable to full loaders
   */
  label?: string;

  /**
   * The sub-label of the Loader.
   * Only applicable to full loaders.
   *
   * Pass this if you want to show extra contextual information to the user.
   */
  subLabel?: string;

  /**
   * The variant of loader to use for the loader.
   * @default "bar"
   */
  variant?: LoaderVariant;
}

/**
 * Loaders are used to display states that need time to be able to be presented
 * in the interface, such as showing a long list, or performing an action such
 * as uploading something. We have 2 types, outlined below.
 *
 * This loader is used when you are replacing a section on a page that contains
 * multiple elements. Examples include things like lists, full cards, & modals.
 * If you are showing an individual item being loaded, use the 3-dot format
 * outlined below.
 *
 * This loader is used when you are showing an individual item that is being
 * loaded. It’s used when you have to show how one item is loading against
 * multiple similar items. Examples include a single line-item in a list.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=2442%3A5656&t=TJpvgjcJzv27NfVe-0
 */
export const Loader = React.forwardRef(
  (
    { className, color = 'primary', label, subLabel, variant = 'bar' }: LoaderProps,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    const loaderColor = LOADER_COLORS[color];

    if (variant === 'dot') {
      // The dots are drawn entirely with box-shadow off a single element that is
      // pushed far off-screen to the left; each shadow offset pulls a dot back to
      // the center. The keyframes reference --loader-color so the pulse animates
      // in the requested color.
      return (
        <Box
          ref={ref}
          className={className}
          sx={{
            '--loader-color': loaderColor,
            width: '10px',
            height: '10px',
            borderRadius: '5px',
            backgroundColor: colors.content.default,
            color: colors.content.default,
            position: 'relative',
            left: '-9999px',
            boxShadow: `9999px 0 0 -5px ${colors.content.dark}`,
            animation: 'dotPulse 1.5s infinite linear',
            animationDelay: '0.25s',
            '&::before, &::after': {
              content: '""',
              display: 'inline-block',
              position: 'absolute',
              top: 0,
              width: '10px',
              height: '10px',
              borderRadius: '5px',
              backgroundColor: colors.content.default,
              color: colors.content.default,
            },
            '&::before': {
              boxShadow: `9984px 0 0 -5px ${colors.content.dark}`,
              animation: 'dotPulseBefore 1.5s infinite linear',
              animationDelay: '0s',
            },
            '&::after': {
              boxShadow: `10014px 0 0 -5px ${colors.content.dark}`,
              animation: 'dotPulseAfter 1.5s infinite linear',
              animationDelay: '0.5s',
            },
            '@keyframes dotPulseBefore': {
              '0%': { boxShadow: '9984px 0 0 -5px var(--loader-color)' },
              '30%': { boxShadow: '9984px 0 0 2px var(--loader-color)' },
              '60%, 100%': { boxShadow: '9984px 0 0 -5px var(--loader-color)' },
            },
            '@keyframes dotPulse': {
              '0%': { boxShadow: '9999px 0 0 -5px var(--loader-color)' },
              '30%': { boxShadow: '9999px 0 0 2px var(--loader-color)' },
              '60%, 100%': { boxShadow: '9999px 0 0 -5px var(--loader-color)' },
            },
            '@keyframes dotPulseAfter': {
              '0%': { boxShadow: '10014px 0 0 -5px var(--loader-color)' },
              '30%': { boxShadow: '10014px 0 0 2px var(--loader-color)' },
              '60%, 100%': { boxShadow: '10014px 0 0 -5px var(--loader-color)' },
            },
          }}
        />
      );
    }

    return (
      <Box
        ref={ref}
        className={className}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'center',
            width: '100%',
          }}
        >
          <Text variant="heading4" bold>
            {label}
          </Text>
          {subLabel && <Text variant="body1">{subLabel}</Text>}
        </Box>
        <Box
          sx={{
            width: '200px',
            height: '4px',
            backgroundColor: colors.border.default,
            overflow: 'hidden',
            borderRadius: 1,
          }}
        >
          <Box
            // `loading-moving-bar` + `color-${color}` are retained as styling
            // hooks: the patient app overrides the primary bar with its own brand
            // color via `.loading-moving-bar.color-primary`.
            className={`loading-moving-bar color-${color}`}
            sx={{
              animation: 'loadingBar 1s infinite linear',
              '@keyframes loadingBar': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(120%)' },
              },
              width: '100%',
              height: '100%',
              background: loaderColor,
            }}
          />
        </Box>
      </Box>
    );
  }
);

Loader.displayName = 'Loader';
